import {
  Button,
  Card,
  Chip,
  InfoTooltip,
  Search,
  Text,
  TextLink,
  Toolbar,
  VSpacer,
} from '@/components/DesignSystem';
import { Filter, FilterSelections } from '@/components/DesignSystem/Toolbar/interfaces';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { InfiniteScroll } from '@/components/shared/InfiniteScroll';
import { QueryKeys } from '@/constants/QueryKeys';
import { useSearch } from '@/hooks/useSearch';
import { LinkedAccountsDialog } from '@/pages/FarmerList/LinkedAccountsDialog';
import {
  ViewCustomerDetailsFullScreenModal,
} from '@/pages/RetailerOverview/ViewCustomerDetailsFullScreenModal';
import { Color } from "@/themes/MUITheme/palette";
import { ScheduledNotificationApi } from '@/utilities/api/ScheduledNotificationApi';
import { downloadCsv } from '@/utilities/Export';
import { ScheduledNotificationEndpoint } from '@api/endpoints';
import { ApiUser as BaseApiUser } from '@api/interfaces';
import {
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  useTheme,
} from '@mui/material';
import { NotificationStatus } from "@shared/enums/NotificationStatus";
import { DeepLinkUrls } from '@shared/enums/ScheduledNotificationType';
import { FilterOptions, CSVRecipientData } from '@shared/interfaces/ScheduledNotification';
import {
  formatDate,
  formatMediumDate,
  formatNumber,
  formatPercent,
  formatTime,
  getAccountName,
} from '@shared/utilities';
import i18next from 'i18next';
import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery, useMutation, useQuery } from 'react-query';

export interface ExtendedApiUser extends BaseApiUser {
  outstandingPointsIssued?: number,
}

interface ExtendedRecipientData extends ScheduledNotificationEndpoint.GetRecipients.RecipientData {
  message?: string,
  sentAt?: string,
  deepLink?: string,
  status?: string,
  twilioErrorCode?: number | null,
}

export interface NotificationRecipientsContentProps {
  onExportDialogClose: () => void,
  options: FilterOptions,
  csvRecipientData: CSVRecipientData,
  retailerId: string,
  scheduledNotificationId?: string,
  showExportDialog: boolean,
}

const formatDateTime = (
  dateInput: string | Date | null | undefined,
): string => {
  if (!dateInput) {
    return '';
  }

  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
  if (isNaN(date.getTime())) {
    return '';
  }

  return formatDate(date, i18next) + ' ' + formatTime(date, i18next);
};

const getDeepLinkDisplayName = (
  deepLinkValue: string | null | undefined,
): string => {
  if (!deepLinkValue) {
    return '';
  }

  const deepLinkEntry = Object.entries(DeepLinkUrls).find(([, value]) => value === deepLinkValue);
  return deepLinkEntry ? deepLinkEntry[1] : deepLinkValue;
};

export const NotificationRecipientsContent = ({
  onExportDialogClose,
  options,
  csvRecipientData,
  retailerId,
  scheduledNotificationId,
  showExportDialog,
}: NotificationRecipientsContentProps) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const { search, setSearch, debouncedSearch } = useSearch();
  const {
    search: customerSearch,
    setSearch: setCustomerSearch,
  } = useSearch();
  const [customerFilterSelections, setCustomerFilterSelections] =
    useState<FilterSelections>(() => new Map());
  const [
    selectedLinkedAccountsCustomer,
    setSelectedLinkedAccountsCustomer,
  ] = useState<ExtendedApiUser>();
  const [selectedViewAccountCustomer, setSelectedViewAccountCustomer] =
    useState<ExtendedApiUser>();
  const [sortBy, setSortBy] = useState<string>('name');
  const [sortDesc, setSortDesc] = useState(false);
  const [isDefaultSort, setIsDefaultSort] = useState(true);

  const tableRef = useRef<HTMLDivElement>(null);
  const [tableHeight, setTableHeight] = useState(0);

  useLayoutEffect(() => {
    const measure = () => {
      if (!tableRef.current) {
        return;
      }
      const { top } = tableRef.current.getBoundingClientRect();
      setTableHeight(window.innerHeight - top - 24);
    };

    measure();

    const observer = new ResizeObserver(measure);
    if (tableRef.current?.parentElement) {
      observer.observe(tableRef.current.parentElement);
    }
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  const selectedCustomerIds = Array.from(customerFilterSelections?.get('customer') ?? []);

  const {
    data: recipientFiltersPages,
    fetchNextPage: fetchNextFilterPage,
    hasNextPage: hasNextFilterPage,
    isFetchingNextPage: isFetchingNextFilterPage,
    isLoading: areCustomersInitiallyLoading,
  } = useInfiniteQuery(
    [
      QueryKeys.LIST_RECIPIENTS_FILTER,
      options,
      csvRecipientData,
      retailerId,
      scheduledNotificationId,
      customerSearch,
    ],
    ({ pageParam }) => ScheduledNotificationApi.getRecipientFilters({
      limit: 50,
      options,
      csvRecipientData,
      retailerId,
      scheduledNotificationId,
      page: pageParam,
      search: customerSearch || undefined,
    }),
    {
      enabled: !!retailerId,
      getNextPageParam: (lastPage) => (
        lastPage.lastPage > lastPage.page ? lastPage.page + 1 : undefined
      ),
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
    },
  );

  const recipientsForFiltering = useMemo(
    () => recipientFiltersPages?.pages.flatMap(p => p.data) ?? [],
    [recipientFiltersPages],
  );

  const selectedCustomerRecipientsDetails = useMemo(() => {
    if (selectedCustomerIds.length === 0) {
      return csvRecipientData;
    }

    // TODO: we should instead pass customerIds in the options body instead
    // of using recipientDetails to filter.
    return recipientsForFiltering
      .filter(r => selectedCustomerIds.includes(r.id))
      .map(r => ({ userId: r.id, phone: '' }));
  }, [selectedCustomerIds, recipientsForFiltering, csvRecipientData]);

  const customerFilter: Filter[] = useMemo(() => ([{
    id: 'customer',
    infiniteScrollOptions: {
      fetchMoreItems: fetchNextFilterPage,
      hasMore: hasNextFilterPage ?? false,
      isLoading: isFetchingNextFilterPage,
    },
    isChipLabelVerbose: true,
    isLoading: areCustomersInitiallyLoading,
    label: t($ => $.common.customer),
    onSearch: (searchValue) => setCustomerSearch(searchValue),
    options: recipientsForFiltering?.map(r => ({
      id: r.id,
      label: r.name,
    })) ?? [],
    search: customerSearch,
    selectionMethod: 'multi-select',
  }] as Filter[]), [
    areCustomersInitiallyLoading,
    customerSearch,
    fetchNextFilterPage,
    hasNextFilterPage,
    isFetchingNextFilterPage,
    recipientsForFiltering,
    setCustomerSearch,
  ]);

  const recipientsQueryKey = useMemo(() => [
    QueryKeys.LIST_RECIPIENTS,
    options,
    retailerId,
    selectedCustomerRecipientsDetails,
    debouncedSearch,
    scheduledNotificationId,
    sortBy,
    sortDesc,
  ], [
    options,
    retailerId,
    selectedCustomerRecipientsDetails,
    debouncedSearch,
    scheduledNotificationId,
    sortBy,
    sortDesc,
  ]);

  const {
    data: recipients,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteQuery(
    recipientsQueryKey,
    async ({ pageParam }) => {
      return ScheduledNotificationApi.getRecipients({
        limit: 20,
        options,
        page: pageParam,
        csvRecipientData: selectedCustomerRecipientsDetails,
        retailerId,
        search: debouncedSearch,
        sort: sortBy,
        sortDesc,
        scheduledNotificationId,
      });
    },
    {
      enabled: !!retailerId,
      getNextPageParam: (lastPage) => (
        lastPage.lastPage > lastPage.page
          ? lastPage.page + 1
          : undefined
      ),
      keepPreviousData: true,
    },
  );

  const {
    mutateAsync: exportRecipients,
    isLoading: isExporting,
  } = useMutation(
    async () => {
      try {
        const csv = await ScheduledNotificationApi.exportRecipient(
          {
            options,
            csvRecipientData: selectedCustomerRecipientsDetails,
            search: debouncedSearch,
            sort: sortBy,
            sortDesc: sortDesc,
            retailerId,
          },
          scheduledNotificationId,
        );
        const dateString = new Date().toISOString().slice(0, 10);
        downloadCsv(csv, `notification-recipients-export-${dateString}`);
      } catch (error) {
        console.error('Error exporting recipients:', error);
      }
    },
  );

  const { data: notificationStats } = useQuery(
    [
      QueryKeys.GET_NOTIFICATION_STATS,
      retailerId,
      options,
      csvRecipientData,
      scheduledNotificationId,
    ],
    () => ScheduledNotificationApi.getStats(scheduledNotificationId!),
    {
      enabled: !!scheduledNotificationId,
      staleTime: 5 * 60 * 1000,
    },
  );

  const handleSort = (newSortBy: string) => {
    if (newSortBy === sortBy && !isDefaultSort) {
      if (sortDesc) {
        setSortDesc(false);
      } else {
        setSortBy('name');
        setSortDesc(false);
        setIsDefaultSort(true);
      }
    } else {
      setSortBy(newSortBy);
      setSortDesc(true);
      setIsDefaultSort(false);
    }
  };

  const getCustomerCount = (): string => {
    if (isLoading) {
      return t($ => $.common.loadingEllipsis);
    }

    const totalRecipients = recipients?.pages[0]?.total || 0;
    return t(
      $ => $.notificationBuilder.table.recipientCount,
      { count: totalRecipients },
    );
  };

  const renderNotificationSummary = () => {
    if (!scheduledNotificationId) {
      return null;
    }

    const metadataItems = [
      {
        label: t($ => $.notificationBuilder.modal.recipients.table.deepLink),
        testID: 'notification-summary-deep-link',
        value: getDeepLinkDisplayName(notificationStats?.deepLink) || '-',
      },
      {
        label: t($ => $.notificationBuilder.modal.recipients.table.sendDateTime),
        testID: 'notification-summary-send-datetime',
        value: formatDateTime(notificationStats?.sentAt) || '-',
      },
    ];

    const deliveryStatItems = [
      {
        color: 'primary' as const,
        label: t($ => $.notificationBuilder.modal.recipients.stats.sent),
        testID: 'notification-summary-sent',
        value: formatNumber(notificationStats?.deliveryStats.sent ?? 0, i18n),
      },
      {
        color: 'success' as const,
        label: t($ => $.notificationBuilder.modal.recipients.stats.delivered),
        testID: 'notification-summary-delivered',
        value: formatNumber(notificationStats?.deliveryStats.delivered ?? 0, i18n),
      },
      {
        color: 'error' as const,
        label: t($ => $.notificationBuilder.modal.recipients.stats.failed),
        testID: 'notification-summary-failed',
        value: formatNumber(notificationStats?.deliveryStats.failed ?? 0, i18n),
      },
      {
        color: 'warning' as const,
        label: t($ => $.notificationBuilder.modal.recipients.stats.optedOut),
        testID: 'notification-summary-opted-out',
        value: formatNumber(notificationStats?.deliveryStats.optedOut ?? 0, i18n),
      },
      {
        color: 'error' as const,
        label: t($ => $.notificationBuilder.modal.recipients.stats.substitutionFailed),
        testID: 'notification-summary-substitution-failed',
        value: formatNumber(notificationStats?.deliveryStats.substitutionFailed ?? 0, i18n),
      },
    ];

    const engagementRateItems = [
      {
        label: t($ => $.notificationBuilder.modal.recipients.stats.linkOpenRate5h),
        testID: 'notification-summary-opened-5hrs',
        value: formatPercent((notificationStats?.linkOpenRates.within5Hours ?? 0) * 100, i18next),
      },
      {
        label: t($ => $.notificationBuilder.modal.recipients.stats.linkOpenRate24h),
        testID: 'notification-summary-opened-24hrs',
        value: formatPercent((notificationStats?.linkOpenRates.within24Hours ?? 0) * 100, i18next),
      },
    ];

    const cardContentStyle = {
      padding: '8px 12px',
      '&:last-child': { paddingBottom: '8px' },
    };

    return (
      <Stack alignItems="flex-start" direction="row" flexWrap="wrap" gap={1}>
        <Stack alignItems="center" direction="row" flexWrap="nowrap" gap={1}>
          {metadataItems.map(({ label, testID, value }) => (
            <Card cardContentStyle={cardContentStyle} key={testID} testID={testID}>
              <Text
                category="body-small"
                sx={{ color: 'text.secondary' }}
                testID={`${testID}-label`}
              >
                {label}
              </Text>
              <VSpacer size="1" />
              <Text category="title-medium" testID={`${testID}-value`}>
                {value}
              </Text>
            </Card>
          ))}
        </Stack>
        <Card cardContentStyle={cardContentStyle} testID="delivery-stats-card">
          <Stack alignItems="center" direction="row" flexWrap="nowrap" gap={1} spacing={2}>
            {deliveryStatItems.map(({ color, label, testID, value }) => (
              <Stack key={testID}>
                <Text
                  category="body-small"
                  sx={{ color: 'text.secondary' }}
                  testID={`${testID}-label`}
                >
                  {label}
                </Text>
                <VSpacer size="1" />
                <Text
                  category="title-medium"
                  sx={{ color: `${color}.main` }}
                  testID={`${testID}-value`}
                >
                  {value}
                </Text>
              </Stack>
            ))}
          </Stack>
        </Card>
        <Card cardContentStyle={cardContentStyle} testID="link-open-rate-card">
          <Stack alignItems="center" direction="row" flexWrap="nowrap" gap={1} spacing={2}>
            {engagementRateItems.map(({ label, testID, value }) => (
              <Stack key={testID}>
                <Text
                  category="body-small"
                  sx={{ color: 'text.secondary' }}
                  testID={`${testID}-label`}
                >
                  {label}
                </Text>
                <VSpacer size="1" />
                <Text category="title-medium" testID={`${testID}-value`}>
                  {value}
                </Text>
              </Stack>
            ))}
          </Stack>
        </Card>
      </Stack>
    );
  };

  const renderTableData = () => {
    const baseColSpan = 9;
    const additionalColSpan = scheduledNotificationId ? 2 : 0;
    const totalColSpan = baseColSpan + additionalColSpan;

    if (recipients?.pages?.every(page => page?.data.length === 0)) {
      return (
        <TableRow>
          <TableCell align="center" colSpan={totalColSpan}>
            <Text>{t($ => $.common.error.noDataAvailable)}</Text>
          </TableCell>
        </TableRow>
      );
    }

    const getNotificationStatus = (notificationData: ExtendedRecipientData) => {
      if (!notificationData.status) {
        return {
          color: 'info',
          text: t($ => $.notificationBuilder.modal.recipients.pending),
        };
      }
      if (notificationData.status === NotificationStatus.OptedOut) {
        return {
          color: 'warning',
          text: t($ => $.notificationBuilder.modal.recipients.optedOut),
        };
      }
      if (notificationData.status === NotificationStatus.Failed) {
        return {
          color: 'error',
          text: t($ => $.notificationBuilder.modal.recipients.failure),
        };
      }
      if (notificationData.status === NotificationStatus.SubstitutionFailed) {
        return {
          color: 'error',
          text: t($ => $.notificationBuilder.modal.recipients.substitutionFailed),
        };
      }
      return {
        color: 'success',
        text: t($ => $.notificationBuilder.modal.recipients.success),
      };
    };

    return (
      (recipients?.pages?.flatMap(({ data }) => data) ?? [])
        .map(
          (
            baseCustomer: ScheduledNotificationEndpoint.GetRecipients.RecipientData,
          ) => {
            const customer = baseCustomer.customer;
            const extendedData = scheduledNotificationId ?
              (baseCustomer as ExtendedRecipientData) :
              null;

            const notificationStatus = extendedData ? getNotificationStatus(extendedData) : null;
            return (
              <TableRow key={customer.id}>
                <TableCell sx={{
                  backgroundColor: theme.palette.background.paper,
                  boxShadow: '2px 0px 4px rgba(0, 0, 0, 0.05)',
                  left: 0,
                  position: 'sticky',
                }}>
                  {getAccountName(customer)}
                  <Text category="body-xsmall" sx={{ color: theme.palette.text.secondary }}>
                    ID: #{customer?.externalId}
                  </Text>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {baseCustomer.tierName || '-'}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {baseCustomer?.farmerRewards?.tierName || '-'}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {baseCustomer?.farmerRewards?.segmentsRemaining || 0}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {formatNumber(baseCustomer.outstandingPointsIssued || 0, i18n)}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {baseCustomer.mostRecentRedemption
                    ? formatMediumDate(baseCustomer.mostRecentRedemption, i18n)
                    : ''
                  }
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {baseCustomer.mostRecentPurchase
                    ? formatMediumDate(baseCustomer.mostRecentPurchase, i18n)
                    : ''
                  }
                </TableCell>
                {notificationStatus && extendedData && (
                  <>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {formatDateTime(extendedData.deepLinkOpenedAt)}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Chip
                        color={notificationStatus.color as Color}
                        label={
                          <Text category="label-small" style={{ textTransform: 'uppercase' }}>
                            {notificationStatus.text}
                          </Text>
                        }
                        testID={`delivery-status-chip-${customer.id}`}
                        variant="filled"
                      />
                    </TableCell>
                  </>
                )}
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {!baseCustomer.linkedAccounts?.length ? (
                    ''
                  ) : (
                    <TextLink
                      onClick={(e) => {
                        e.preventDefault();
                        setSelectedLinkedAccountsCustomer(customer);
                      }}
                      testID={`${customer.id}-linked-accounts-button`}
                    >
                      {t(
                        $ => $.notificationBuilder.modal.recipients.linkedAccountsCount,
                        { count: baseCustomer.linkedAccounts.length },
                      )}
                    </TextLink>
                  )}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Button
                    onClick={() => setSelectedViewAccountCustomer(customer)}
                    size="small"
                    square
                    testID={`${customer.id}-view-account-button`}
                  >
                    {t($ => $.common.actions.viewAccount)}
                  </Button>
                </TableCell>
              </TableRow>
            );
          })
    );
  };

  return (
    <>
      {renderNotificationSummary()}
      {scheduledNotificationId && <VSpacer size="4" />}
      <Stack alignItems="center" direction="row" flexWrap="wrap" gap={2}>
        <Search
          onChangeText={setSearch}
          testID="notification-recipients-search"
          value={search}
          width="440px"
        />
        <Toolbar
          filterSelections={customerFilterSelections}
          filters={customerFilter}
          hideSearch
          onChange={({ selections }) => {
            setCustomerFilterSelections(selections ?? new Map());
          }}
          retainSelectionsOnFilterChange
          testID="notification-recipients-customer-filter"
        />
      </Stack>
      <VSpacer size="3" />
      <Text category="body-medium">
        {getCustomerCount()}
      </Text>
      <VSpacer size="2" />
      <div ref={tableRef} />
      <InfiniteScroll
        fetchMoreItems={fetchNextPage}
        hasMore={hasNextPage ?? true}
        height={tableHeight > 0 ? `${tableHeight}px` : '100vh'}
        isLoading={isLoading || isFetching || isFetchingNextPage}
        key="notification-recipients-scroll"
        sx={{
          overflowX: 'scroll !important',
          overflowY: 'scroll !important',
          scrollbarWidth: 'auto',
          '&::-webkit-scrollbar': {
            width: '12px !important',
            height: '12px !important',
            backgroundColor: '#f0f0f0',
          },
          '&::-webkit-scrollbar-corner': {
            backgroundColor: '#f0f0f0',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#c1c1c1',
            borderRadius: '6px',
            '&:hover': {
              backgroundColor: '#a8a8a8',
            },
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: '#f0f0f0',
            borderRadius: '6px',
          },
        }}
      >
        <Table sx={{ '& .MuiTableCell-root': { padding: '4px 8px' } }}>
          <TableHead sx={{
            backgroundColor: theme.palette.background.paper,
            position: 'sticky',
            top: 0,
            zIndex: 1000,
          }}>
            <TableRow>
              <TableCell
                sx={{
                  backgroundColor: 'rgb(245 245 245) !important',
                  boxShadow: '2px 0px 4px rgba(0, 0, 0, 0.05)',
                  left: 0,
                  position: 'sticky',
                  zIndex: 1000,
                }}
                variant="head"
              >
                <TableSortLabel
                  active={sortBy === 'name' && !isDefaultSort}
                  direction={
                    sortBy === 'name' && !isDefaultSort
                      ? (sortDesc ? 'desc' : 'asc')
                      : 'desc'
                  }
                  onClick={() => handleSort('name')}
                >
                  {t($ => $.common.filters.account)}
                </TableSortLabel>
              </TableCell>
              <TableCell variant="head">
                <Stack alignItems="center" direction="row">
                  {t($ => $.notificationBuilder.modal.recipients.table.currentTier)}
                  <Stack alignItems="center" direction="column">
                    <InfoTooltip color="info">
                      {t($ => $.overview.kpis.currentStatusTooltip)}
                    </InfoTooltip>
                    <TableSortLabel
                      active={sortBy === 'rankCurrent'}
                      direction={
                        sortBy === 'rankCurrent'
                          ? (sortDesc ? 'desc' : 'asc')
                          : 'desc'
                      }
                      onClick={() => handleSort('rankCurrent')}
                    />
                  </Stack>
                </Stack>
              </TableCell>
              <TableCell variant="head">
                <Stack alignItems="center" direction="row">
                  {t($ => $.overview.tierProgress.reEarnedTier)}
                  <Stack alignItems="center" direction="column">
                    <InfoTooltip color="info">
                      {t($ => $.overview.tierProgress.reEarnedTierTooltip)}
                    </InfoTooltip>
                    <TableSortLabel
                      active={sortBy === 'reEarnedTier'}
                      direction={
                        sortBy === 'reEarnedTier'
                          ? (sortDesc ? 'desc' : 'asc')
                          : 'desc'
                      }
                      onClick={() => handleSort('reEarnedTier')}
                    />
                  </Stack>
                </Stack>
              </TableCell>
              <TableCell variant="head">
                <Stack alignItems="center" direction="row">
                  {t(
                    $ => $.notificationBuilder.modal.recipients.table.categoriesToReachNextTier,
                  )}
                  <Stack alignItems="center" direction="column">
                    <InfoTooltip color="info">
                      {t(
                        $ => $.overview.tierProgress.categoriesToReachNextTierTooltip,
                      )}
                    </InfoTooltip>
                    <TableSortLabel
                      active={sortBy === 'segmentsRemaining'}
                      direction={
                        sortBy === 'segmentsRemaining'
                          ? (sortDesc ? 'desc' : 'asc')
                          : 'desc'
                      }
                      onClick={() => handleSort('segmentsRemaining')}
                    />
                  </Stack>
                </Stack>
              </TableCell>
              <TableCell variant="head">
                <TableSortLabel
                  active={sortBy === 'outstandingPointsIssued'}
                  direction={
                    sortBy === 'outstandingPointsIssued'
                      ? (sortDesc ? 'desc' : 'asc')
                      : 'desc'
                  }
                  onClick={() => handleSort('outstandingPointsIssued')}
                >
                  {t($ => $.notificationBuilder.modal.recipients.table.outstandingPoints)}
                </TableSortLabel>
              </TableCell>
              <TableCell variant="head">
                <TableSortLabel
                  active={sortBy === 'mostRecentRedemption'}
                  direction={
                    sortBy === 'mostRecentRedemption'
                      ? (sortDesc ? 'desc' : 'asc')
                      : 'desc'
                  }
                  onClick={() => handleSort('mostRecentRedemption')}
                >
                  {t(
                    $ => $.notificationBuilder.modal.recipients.table.mostRecentRedemption,
                  )}
                </TableSortLabel>
              </TableCell>
              <TableCell variant="head">
                <TableSortLabel
                  active={sortBy === 'mostRecentPurchase'}
                  direction={
                    sortBy === 'mostRecentPurchase'
                      ? (sortDesc ? 'desc' : 'asc')
                      : 'desc'
                  }
                  onClick={() => handleSort('mostRecentPurchase')}
                >
                  {t(
                    $ => $.notificationBuilder.modal.recipients.table.mostRecentPurchase,
                  )}
                </TableSortLabel>
              </TableCell>
              {scheduledNotificationId && (
                <>
                  <TableCell variant="head">
                    {t($ => $.notificationBuilder.modal.recipients.table.deepLinkDateTime)}
                  </TableCell>
                  <TableCell variant="head">
                    {t($ => $.notificationBuilder.modal.recipients.table.deliveryStatus)}
                  </TableCell>
                </>
              )}
              <TableCell variant="head">
                {t($ => $.notificationBuilder.modal.recipients.table.linkedAccounts)}
              </TableCell>
              <TableCell variant="head" />
            </TableRow>
          </TableHead>
          <TableBody>
            {renderTableData()}
          </TableBody>
        </Table>
      </InfiniteScroll>

      {showExportDialog && (
        <ConfirmDialog
          cancelText={t($ => $.common.actions.goBack)}
          confirmText={t($ => $.common.actions.export)}
          isLoading={isExporting}
          onCancel={onExportDialogClose}
          onConfirm={async () => {
            await exportRecipients();
            onExportDialogClose();
          }}
          open
          showCloseButton={false}
          testID="export-confirmation-dialog"
        >
          {t($ => $.notificationBuilder.modal.recipients.exportConfirmation)}
        </ConfirmDialog>
      )}

      {!!selectedLinkedAccountsCustomer && (
        <LinkedAccountsDialog
          customer={selectedLinkedAccountsCustomer}
          onClose={() => setSelectedLinkedAccountsCustomer(undefined)}
          onViewAccount={(customer) => {
            setSelectedViewAccountCustomer(customer);
          }}
          show
        />
      )}

      {!!selectedViewAccountCustomer && (
        <ViewCustomerDetailsFullScreenModal
          id={selectedViewAccountCustomer.id}
          onClose={() => setSelectedViewAccountCustomer(undefined)}
          onViewAccount={(customer) => {
            setSelectedViewAccountCustomer(customer);
          }}
        />
      )}
    </>
  );
};
